import React, { useState, useEffect } from "react";
import {
  Mail,
  Phone,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
  Plus,
} from "lucide-react";
import { API_URL } from "../../config/api";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
 
// -------------------------------------------------------
// Student Card Component
// -------------------------------------------------------
const StudentCard = ({ data }) => {
  const navigate = useNavigate();
 
  if (!data) {
    return (
      <div className="w-full bg-white rounded-lg p-4 text-red-500">
        Invalid student data
      </div>
    );
  }
 
  const {
    user_id = "N/A",
    full_name = "",
    first_name = "",
    last_name = "",
    email = "Not provided",
    mobile_number = "Not provided",
    avatar_url = "",
    course_name = "N/A",
    course_created_at = "",
    schedule_start_date = "",
  } = data;
 
  const displayName =
    first_name && last_name
      ? `${first_name} ${last_name}`
      : full_name || "Unknown User";
 
  const formattedDate = schedule_start_date
    ? new Date(schedule_start_date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      })
    : "N/A";
 
  const handleViewDetails = () => {
    navigate(`/ins_student/student_details/${user_id}`, {
      state: {
        ...data,
      },
    });
  };
 
  return (
    <div className="w-full flex flex-col bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      {/* Header Section */}
      <div className="p-4 flex gap-3">
        <img
          className="w-12 h-12 rounded object-cover flex-shrink-0"
          src={
            avatar_url ||
            "https://placehold.co/48x48/4AACC5/white?text=" +
              displayName.charAt(0)
          }
          alt={displayName}
          onError={(e) => {
            e.target.src =
              "https://placehold.co/48x48/4AACC5/white?text=" +
              displayName.charAt(0);
          }}
        />
        <div className="flex flex-col items-start gap-1 min-w-0 flex-1">
          <div className="text-[#4AACC5] text-xs font-normal">#{user_id}</div>
          <div className="text-zinc-800 text-sm font-semibold ">
            {displayName}
          </div>
          <div className="text-zinc-600 text-xs leading-tight">
            Program: {course_name}
          </div>
        </div>
      </div>
 
      {/* Contact Details Section */}
      <div className="px-4 pb-4 flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 flex-shrink-0 text-gray-500" />
          <div className="text-[#4AACC5] text-sm truncate">{email}</div>
        </div>
 
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 flex-shrink-0 text-gray-500" />
          <div className="text-zinc-800 text-sm">{mobile_number}</div>
        </div>
 
        <div className="flex items-center gap-2 justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 flex-shrink-0 text-gray-500" />
            <div className="text-zinc-800 text-sm">{formattedDate}</div>
          </div>
 
          <button
            className="flex items-center text-[#4AACC5] text-sm font-medium hover:opacity-80 transition-opacity whitespace-nowrap"
            onClick={handleViewDetails}
          >
            View Details
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
};
 
// -------------------------------------------------------
// MAIN COMPONENT
// -------------------------------------------------------
export default function StudentManagement() {
  const navigate = useNavigate();
 
  // State Management
  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(null);
 
  const formatDateForAPI = (date) => {
    if (!date) return null;
 
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
 
    return `${year}-${month}-${day}`; // local date, no timezone shift
  };
 
  // Fetch Students from API
  const fetchStudents = async () => {
    setLoading(true);
    setError(null);
 
    try {
      const token = localStorage.getItem("access_token");
 
      if (!token) {
        throw new Error("No access token found. Please log in.");
      }
 
      const params = new URLSearchParams({
        page: currentPage,
        limit: resultsPerPage,
      });
 
      // ✅ IMPROVED: Universal search parameter
      if (searchTerm) {
        const trimmedSearch = searchTerm.trim();
       
        // Send as generic "search" parameter - backend decides what to search
        params.append("search", trimmedSearch);
       
        // ALSO send specific parameters for better compatibility
        const isPhoneNumber = /^\d+$/.test(trimmedSearch);
       
        if (isPhoneNumber) {
          // Backend might use any of these parameter names
          params.append("mobile_number", trimmedSearch);
          params.append("mobile", trimmedSearch);
          params.append("phone", trimmedSearch);
        } else {
          params.append("name", trimmedSearch);
        }
      }
 
      if (startDate) {
        params.append("start_date", formatDateForAPI(startDate));
      }
 
      const url = `${API_URL}/courses/instructor/students?${params.toString()}`;
 
      console.log("Search Term:", searchTerm);
      console.log(" Is Phone:", /^\d+$/.test(searchTerm));
      console.log(" API URL:", url);
 
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
 
      if (!response.ok) {
        throw new Error(
          `Failed to fetch students: ${response.status} ${response.statusText}`
        );
      }
 
      const json = await response.json();
 
      console.log("✅ API Response:", json);
      console.log("📊 Students Found:", json.data?.length || 0);
 
      setStudents(Array.isArray(json.data) ? json.data : []);
      setTotal(json.total || 0);
    } catch (err) {
      console.error("❌ Error fetching students:", err);
      setError(err.message);
      setStudents([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };
 
  // ✅ Debounced search with useEffect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchStudents();
    }, 300); // Wait 300ms after user stops typing
 
    return () => clearTimeout(timeoutId);
  }, [searchTerm, startDate, currentPage, resultsPerPage]);
 
  // Calculate total pages
  const totalPages = Math.ceil(total / resultsPerPage) || 1;
 
  // Handle search with debouncing
  const handleSearchChange = (e) => {
    setCurrentPage(1);
    setSearchTerm(e.target.value);
  };
 
  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };
 
  // Handle results per page change
  const handleResultsPerPageChange = (e) => {
    setResultsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };
 
  // Render page numbers
  const renderPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 3;
 
    if (totalPages <= maxPagesToShow + 1) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            className={`w-8 h-8 rounded-lg border flex items-center justify-center text-sm transition-colors ${
              currentPage === i
                ? "bg-[#4AACC5] border-[#4AACC5] text-white"
                : "bg-white text-zinc-800 border-stone-300 hover:bg-gray-50"
            }`}
          >
            {i}
          </button>
        );
      }
    } else {
      // Show first few pages
      for (let i = 1; i <= maxPagesToShow; i++) {
        pages.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            className={`w-8 h-8 rounded-lg border flex items-center justify-center text-sm transition-colors ${
              currentPage === i
                ? "bg-[#4AACC5] border-[#4AACC5] text-white"
                : "bg-white text-zinc-800 border-stone-300 hover:bg-gray-50"
            }`}
          >
            {i}
          </button>
        );
      }
 
      // Show ellipsis
      pages.push(
        <span key="ellipsis" className="text-gray-700 px-2">
          ...
        </span>
      );
 
      // Show last page
      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className={`w-8 h-8 rounded-lg border flex items-center justify-center text-sm transition-colors ${
            currentPage === totalPages
              ? "bg-[#4AACC5] border-[#4AACC5] text-white"
              : "bg-white text-zinc-800 border-stone-300 hover:bg-gray-50"
          }`}
        >
          {totalPages}
        </button>
      );
    }
 
    return pages;
  };
 
  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-zinc-800 text-xl font-bold mb-4">Students</h1>
 
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {/* Search Box */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Name/Mobile"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-60 h-11 px-4 pr-10 bg-white rounded border border-sky-100 text-sm focus:outline-none focus:border-[#4AACC5] transition-colors"
                />
                <Search className="w-4 h-4 absolute right-3 top-3.5 text-gray-400" />
              </div>
 
              {/* Date Filter */}
              <div className="w-60 relative">
                <DatePicker
                  selected={startDate}
                  onChange={(date) => {
                    setCurrentPage(1);
                    setStartDate(date);
                  }}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="Start Date"
                  calendarStartDay={0}
                  wrapperClassName="w-full block"
                  className="block w-full h-11 px-4 bg-white rounded border border-sky-100 text-sm text-left focus:outline-none focus:ring-2 focus:ring-[#4AACC5]"
                />
 
                {startDate && (
                  <button
                    onClick={() => {
                      setStartDate(null);
                      setCurrentPage(1);
                    }}
                    className="absolute right-3 top-3 text-gray-400 hover:text-red-500 text-sm"
                    title="Clear date"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
 
            {/* Add New Button */}
            {/* <button
              className="h-11 px-6 rounded bg-[#4AACC5] text-white text-base font-bold flex items-center gap-2 hover:opacity-90 transition-opacity"
              onClick={() => navigate("/ins_student/add_student")}
            >
              <Plus className="w-4 h-4" />
              Add New
            </button> */}
          </div>
        </div>
 
        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            <strong>Error:</strong> {error}
          </div>
        )}
 
        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4AACC5]"></div>
          </div>
        )}
 
        {/* Student Cards Grid */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {students.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-500 text-lg">
                  {searchTerm || startDate
                    ? "No students found matching your search."
                    : "No students found."}
                </p>
                {searchTerm && (
                  <p className="text-gray-400 text-sm mt-2">
                    Try searching with a different name or mobile number
                  </p>
                )}
              </div>
            ) : (
              students.map((student) => (
                <StudentCard
                  key={student.user_id || Math.random()}
                  data={student}
                />
              ))
            )}
          </div>
        )}
 
        {/* Pagination */}
        {!loading && totalPages > 0 && students.length > 0 && (
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            {/* Page Navigation */}
            <div className="flex items-center gap-2">
              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="w-8 h-8 bg-white rounded-lg border border-stone-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-gray-700" />
              </button>
 
              {/* Page Numbers */}
              {renderPageNumbers()}
 
              {/* Next Button */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="w-8 h-8 bg-white rounded-lg border border-stone-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-gray-700" />
              </button>
            </div>
 
            {/* Results Count and Limit Selector */}
            <div className="flex items-center gap-5">
              <span className="text-gray-700 text-sm font-medium">
                Results {(currentPage - 1) * resultsPerPage + 1}-
                {Math.min(currentPage * resultsPerPage, total)} of {total}
              </span>
 
              <select
                value={resultsPerPage}
                onChange={handleResultsPerPageChange}
                className="w-14 h-8 bg-gray-200 rounded-lg text-gray-700 text-sm font-medium px-2 cursor-pointer hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-[#4AACC5]"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
 